"use client";

import { useMemo, useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useUser, useClerk, useAuth } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { LogoWithText } from "@/components/brand/Logo";
import {
  MenuIcon,
  XIcon,
  OrganizationsIcon,
  SettingsIcon,
  ProfileIcon,
  BillingIcon,
  SignOutIcon,
} from "@/components/icons";
import { cn } from "@/lib/utils";
import { SubscriptionBadge } from "@/components/billing/SubscriptionBadge";
import {
  Home,
  Server,
  Puzzle,
  CreditCard,
  BookOpen,
  Download,
  LayoutDashboard,
} from "lucide-react";

function getUserInitial(nameOrEmail: string | null | undefined) {
  if (!nameOrEmail) return "?";
  const trimmed = nameOrEmail.trim();
  if (!trimmed) return "?";
  return trimmed.slice(0, 1).toUpperCase();
}

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(href + "/");
}

const publicLinks = [
  { href: "/", label: "Home", icon: Home },
  { href: "/servers", label: "Servers", icon: Server },
  { href: "/plugins", label: "Plugins", icon: Puzzle },
  { href: "/plugins/official", label: "Official", icon: Puzzle },
  { href: "/documentation", label: "Docs", icon: BookOpen },
  { href: "/downloads", label: "Downloads", icon: Download },
  { href: "/pricing", label: "Pricing", icon: CreditCard },
];

const authedLinks = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/servers", label: "Servers", icon: Server },
  { href: "/plugins", label: "Plugins", icon: Puzzle },
  { href: "/plugins/official", label: "Official", icon: Puzzle },
  { href: "/documentation", label: "Docs", icon: BookOpen },
  { href: "/downloads", label: "Downloads", icon: Download },
  { href: "/pricing", label: "Pricing", icon: CreditCard },
];

function ProfileDropdown({
  initial,
  displayName,
  username,
  isAdmin,
  onClose,
}: {
  initial: string;
  displayName: string | null;
  username: string | null | undefined;
  isAdmin?: boolean;
  onClose: () => void;
}) {
  const { signOut } = useClerk();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const profileHref = username ? `/profile/${username}` : "/dashboard/settings";

  const dropdownLinks = [
    { href: profileHref, label: "My Profile", icon: ProfileIcon },
    { href: "/dashboard", label: "My Organizations", icon: OrganizationsIcon },
    {
      href: "/dashboard/subscription",
      label: "Subscription",
      icon: BillingIcon,
    },
    { href: "/dashboard/settings", label: "Settings", icon: SettingsIcon },
    ...(isAdmin ? [{ href: "/admin", label: "Admin Dashboard", icon: LayoutDashboard }] : []),
  ];

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-72 origin-top-right rounded-xl border border-border bg-background shadow-xl ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-150"
    >
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
            {initial}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-foreground">
              {username ?? displayName ?? "User"}
            </div>
            {username && displayName && (
              <div className="truncate text-xs text-muted">{displayName}</div>
            )}
          </div>
        </div>
      </div>

      <div className="py-1.5">
        {dropdownLinks.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.href}
              href={link.href}
              onClick={onClose}
              className="group flex items-center gap-3 px-4 py-2 text-sm text-foreground transition-colors hover:bg-foreground/5"
            >
              <Icon className="h-4 w-4 text-muted transition-all duration-200 group-hover:-translate-y-0.5 group-hover:text-primary" />
              {link.label}
            </Link>
          );
        })}
      </div>

      <div className="border-t border-border py-1.5">
        <button
          type="button"
          onClick={() => {
            onClose();
            signOut({ redirectUrl: "/" });
          }}
          className="group flex w-full items-center gap-3 px-4 py-2 text-sm text-red-400 transition-colors hover:bg-foreground/5 hover:text-red-300"
        >
          <SignOutIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          Sign Out
        </button>
      </div>
    </div>
  );
}

interface NavbarProps {
  className?: string;
}

export function Navbar({ className }: NavbarProps) {
  const pathname = usePathname();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  const currentUser = useQuery(api.users.getCurrentUser);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const displayName =
    user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? null;
  const initial = useMemo(
    () => getUserInitial(currentUser?.username ?? displayName),
    [currentUser?.username, displayName]
  );

  const navLinks = isSignedIn ? authedLinks : publicLinks;

  const mobileLinks = isSignedIn
    ? [
        ...authedLinks,
        ...(currentUser?.username
          ? [{ href: `/profile/${currentUser.username}`, label: "Profile" }]
          : []),
        { href: "/dashboard/settings", label: "Settings" },
      ]
    : [
        ...publicLinks,
        { href: "/login", label: "Sign In" },
        { href: "/signup", label: "Get Started" },
      ];

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full border-b border-border bg-background/95 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-background/60",
        className
      )}
    >
      <div className="mx-auto flex min-h-16 max-w-7xl items-center justify-between px-4 py-3 sm:px-6 sm:py-0 lg:px-8">
        <Link
          href="/"
          className="flex items-center gap-2 text-primary transition-opacity hover:opacity-90"
          onClick={() => {
            setMobileOpen(false);
            setProfileOpen(false);
          }}
        >
          <LogoWithText size="sm" />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "group relative flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
                  isActive(pathname, link.href)
                    ? "text-primary"
                    : "text-muted hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-110" />
                {link.label}
                {isActive(pathname, link.href) && (
                  <span className="absolute inset-x-1 -bottom-[13px] h-0.5 rounded-full bg-primary" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          {isLoaded && isSignedIn ? (
            <>
              <div className="hidden md:block">
                <SubscriptionBadge />
              </div>
              <div className="relative">
              <button
                type="button"
                aria-label="Profile menu"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((prev) => !prev)}
                className={cn(
                  "inline-flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-colors",
                  profileOpen
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-foreground hover:bg-foreground/5"
                )}
              >
                {initial}
              </button>

              {profileOpen && (
                <ProfileDropdown
                  initial={initial}
                  displayName={displayName}
                  username={currentUser?.username}
                  isAdmin={currentUser?.isAdmin}
                  onClose={() => setProfileOpen(false)}
                />
              )}
            </div>
            </>
          ) : isLoaded ? (
            <div className="hidden items-center gap-2 sm:flex">
              <Link
                href="/login"
                className="rounded-md px-3 py-2 text-sm font-medium text-muted transition-colors hover:text-foreground"
              >
                Sign In
              </Link>
              <Link
                href="/signup"
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Get Started
              </Link>
            </div>
          ) : (
            <div className="h-9 w-24 animate-pulse rounded-md bg-muted" />
          )}

          <button
            type="button"
            aria-label="Open navigation menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((prev) => !prev)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-foreground/5 md:hidden"
          >
            {mobileOpen ? (
              <XIcon className="h-5 w-5" />
            ) : (
              <MenuIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      <div
        className={cn(
          "md:hidden",
          mobileOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
      >
        <div
          className={cn(
            "fixed inset-0 z-40 bg-black/40 transition-opacity",
            mobileOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setMobileOpen(false)}
        />
        <div
          className={cn(
            "fixed inset-x-0 top-[calc(4rem+env(safe-area-inset-top))] z-50 origin-top rounded-b-2xl border-b border-border bg-background px-4 pb-6 pt-4 shadow-lg transition-all",
            mobileOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-3 opacity-0"
          )}
        >
          <div className="flex flex-col gap-1">
            {mobileLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "rounded-md px-3 py-2.5 text-base font-medium transition-colors",
                  isActive(pathname, link.href)
                    ? "bg-primary/10 text-primary"
                    : "hover:bg-foreground/5",
                  link.href === "/signup" && !isSignedIn
                    ? "mt-2 bg-primary text-center text-primary-foreground hover:bg-primary/90"
                    : ""
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {isSignedIn && (
            <div className="mt-4 border-t border-border pt-4">
              <div className="mb-3 px-3">
                <SubscriptionBadge />
              </div>
              <MobileSignOut />
            </div>
          )}

          <div className="mt-4 flex items-center gap-3 rounded-lg border border-border bg-foreground/5 p-3">
            <LogoWithText size="sm" />
            <div className="text-sm">
              <div className="font-medium">BlockyNetworks</div>
              <div className="text-muted">
                Manage communities and connected servers.
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function MobileSignOut() {
  const { signOut } = useClerk();
  return (
    <button
      type="button"
      onClick={() => signOut({ redirectUrl: "/" })}
      className="group w-full rounded-md px-3 py-2.5 text-left text-base font-medium text-red-400 transition-colors hover:bg-foreground/5 hover:text-red-300"
    >
      <span className="inline-flex items-center gap-2">
        <SignOutIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
        Sign Out
      </span>
    </button>
  );
}
